import { useCallback, useRef, useState } from 'react';
import { nodeInflight, paintBoardSnapshot, planBoardChange } from '../lib/boardHistory';
import type { Board, BoardNode } from '../lib/canvasBoard';

const HISTORY_CAP = 60;
/** Repeated edits of the same kind inside this window collapse into one undo step (e.g. typing). */
const COALESCE_MS = 500;

export type HistoryRecordOptions = {
  /** `false` for progress/status writes that should not become an undo step. */
  record?: boolean;
  /** Edits with the same key within 500 ms share one undo step. */
  coalesce?: string;
};

type Entry = { board: Board; key?: string; at: number };

/**
 * Undo/redo stack of board snapshots (Magnific-style). One gesture — add, connect, move,
 * delete, or a setting — is one step. Uploads, spinners and derived playback do not add steps.
 * Chat, selection, modals and spent credits stay out of it.
 */
export function useBoardHistory() {
  const pastRef = useRef<Entry[]>([]);
  const futureRef = useRef<Entry[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const sync = useCallback(() => {
    setCanUndo(pastRef.current.length > 0);
    setCanRedo(futureRef.current.length > 0);
  }, []);

  /** Call with the board *before* an edit is applied. */
  const record = useCallback((prev: Board, opts?: HistoryRecordOptions) => {
    if (opts?.record === false) return;
    const now = Date.now();
    const last = pastRef.current[pastRef.current.length - 1];
    if (opts?.coalesce && last?.key === opts.coalesce && now - last.at < COALESCE_MS) {
      last.at = now;
      futureRef.current = [];
      sync();
      return;
    }
    pastRef.current.push({ board: prev, key: opts?.coalesce, at: now });
    if (pastRef.current.length > HISTORY_CAP) pastRef.current.splice(0, pastRef.current.length - HISTORY_CAP);
    futureRef.current = [];
    sync();
  }, [sync]);

  /**
   * Decide whether `next` is a new undo step. Returns false when nothing changed.
   * Quiet follow-ups (the file arriving, a render finishing) are painted into older
   * snapshots that still show that same in-flight action.
   */
  const commit = useCallback((prev: Board, next: Board, opts?: HistoryRecordOptions): boolean => {
    const plan = planBoardChange(prev, next);
    if (plan.type === 'same') return false;
    if (plan.type === 'user') {
      if (opts?.record !== false) record(prev, opts);
      return true;
    }
    const paint = (board: Board) => paintBoardSnapshot(board, next, plan);
    for (const entry of pastRef.current) {
      const painted = paint(entry.board);
      if (painted !== entry.board) entry.board = painted;
    }
    for (const entry of futureRef.current) {
      const painted = paint(entry.board);
      if (painted !== entry.board) entry.board = painted;
    }
    return true;
  }, [record]);

  const undo = useCallback((current: Board): Board | null => {
    const entry = pastRef.current.pop();
    if (!entry) return null;
    futureRef.current.push({ board: current, at: Date.now() });
    sync();
    return keepLiveNodes(entry.board, current);
  }, [sync]);

  const redo = useCallback((current: Board): Board | null => {
    const entry = futureRef.current.pop();
    if (!entry) return null;
    pastRef.current.push({ board: current, at: Date.now() });
    sync();
    return keepLiveNodes(entry.board, current);
  }, [sync]);

  const reset = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    sync();
  }, [sync]);

  return { commit, undo, redo, reset, canUndo, canRedo };
}

/**
 * A snapshot taken while a job was already running keeps the live job (so a move doesn't rewind the
 * spinner). A snapshot from before the job — the undo of "generate" — stays as the user left it.
 * Nodes that the snapshot deleted are not revived.
 */
function keepLiveNodes(snapshot: Board, current: Board): Board {
  const live = new Map(current.nodes.filter(nodeInflight).map((n) => [n.id, n]));
  if (!live.size) return snapshot;
  let changed = false;
  const nodes = snapshot.nodes.map((n) => {
    const busy = live.get(n.id);
    if (!busy || !nodeInflight(n)) return n;
    changed = true;
    return { ...busy, x: n.x, y: n.y } as BoardNode;
  });
  return changed ? { ...snapshot, nodes } : snapshot;
}
